import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { isNumber } from '@turf/helpers';
import { FeatureFilter, FiltersMenuService } from '../filters-menu.service';
import { MAX_LIMIT, MIN_LIMIT, SliderValues, STEP } from './slider-values';
import {
  BACHELORS_DEGREE_OR_HIGHER_FRACTION,
  featuresToMultiplyBy100,
  targetAudienceIndustries,
} from '../../../../../../shared/types/feature-data-type';
import { LayerNameFormatPipe } from '../../../../../common-colorscale/layer-name-format.pipe';
import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import { MatSlider, MatSliderRangeThumb } from '@angular/material/slider';
import { IsValidNumberPipe } from '../../../../../../shared/pipes/is-valid-number.pipe';
import { FormatFilterInputValuePipe } from './format-filter-input-value.pipe';
import { NumberFormatterPipe } from '../../../../../../shared/pipes/number-formatter.pipe';
import { IconComponent } from '../../../../../../shared/components/icon/icon.component';
import { IconButtonDirective } from '../../../../../../shared/directives/icon-button.directive';
import { UserAccessService } from '../../../../../../user/access/user-access.service';

@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
  imports: [
    LayerNameFormatPipe,
    IconComponent,
    NgIf,
    MatSlider,
    SliderValues,
    MatSliderRangeThumb,
    IsValidNumberPipe,
    FormatFilterInputValuePipe,
    NgClass,
    NumberFormatterPipe,
    IconButtonDirective,
    AsyncPipe,
  ],
})
export class FilterComponent implements OnInit {
  @Input() filter!: FeatureFilter;
  @Input() activeLevel!: string;
  @Output() removeFilter = new EventEmitter();

  public indexesAndRatiosFeatures = [
    ...featuresToMultiplyBy100,
    ...targetAudienceIndustries,
    BACHELORS_DEGREE_OR_HIGHER_FRACTION,
  ];

  public isMinInputFocused = false;
  public isMaxInputFocused = false;

  constructor(
    public filtersService: FiltersMenuService,
    public accessService: UserAccessService
  ) {}

  ngOnInit() {}

  public updateFilter(
    filter: FeatureFilter,
    value: number,
    isMin: boolean
  ): void {
    const clampedValue = this.clampValue(value);
    isMin ? (filter.min = clampedValue) : (filter.max = clampedValue);

    this.filtersService.updateFilters();
  }

  public emitRemoveFilter(): void {
    this.removeFilter.emit();
  }

  public handleInputFocus(type: 'min' | 'max'): void {
    if (type === 'min') {
      this.isMinInputFocused = true;
    } else {
      this.isMaxInputFocused = true;
    }
  }

  public handleInputBlur(
    type: 'min' | 'max',
    event: Event,
    feature: string
  ): void {
    this.isMinInputFocused = false;
    this.isMaxInputFocused = false;

    const input = event.target as HTMLInputElement;
    let parsedValue = parseFloat(input.value);

    if (!isNaN(parsedValue)) {
      if (this.indexesAndRatiosFeatures.includes(feature)) {
        parsedValue /= 100;
      }

      const clampedValue = this.clampValue(parsedValue);

      if (type === 'min') {
        this.filter.min = clampedValue;
      } else {
        this.filter.max = clampedValue;
      }

      this.filtersService.updateFilters();
    } else {
      // Revert to the last valid internal value if the input is invalid
      input.value = String(
        this.indexesAndRatiosFeatures.includes(feature)
          ? (type === 'min' ? this.filter.min : this.filter.max)! * 100
          : type === 'min'
            ? this.filter.min
            : this.filter.max
      );
    }
  }

  private clampValue(value: number): number {
    let clampedValue = value;
    if (this.filter.filterGlobalMin !== undefined) {
      clampedValue = Math.max(clampedValue, this.filter.filterGlobalMin);
    }
    if (this.filter.filterGlobalMax !== undefined) {
      clampedValue = Math.min(clampedValue, this.filter.filterGlobalMax);
    }
    return clampedValue;
  }

  public handleInput(event: Event): void {
    const input = event.target as HTMLInputElement;

    // Allow only numbers, one optional dot, and an optional leading minus sign
    const sanitizedValue = input.value.replace(/[^0-9.-]/g, '');

    // Ensure there's at most one dot and it comes after the minus sign (if present)
    const parts = sanitizedValue.split('.');
    let newValue =
      parts.length > 2
        ? `${parts[0]}.${parts.slice(1).join('')}`
        : sanitizedValue;

    // Ensure the minus sign appears only at the beginning
    if (newValue.includes('-')) {
      newValue = newValue.replace(/-/g, ''); // Remove all instances of '-'
      newValue = `-${newValue}`; // Re-add '-' to the start
    }

    input.value = newValue;
  }

  protected readonly isNumber = isNumber;
  protected readonly Math = Math;
  protected readonly STEP = STEP;
  protected readonly MIN_LIMIT = MIN_LIMIT;
  protected readonly MAX_LIMIT = MAX_LIMIT;
}
